import i18next from 'i18next';
import type { TModule, TWidget } from 'utility/types/dataProvider';
import { getKpiPercentageChange, getDefaultPrecision } from '../widgets/KPIs/utils';
import { formatChartValue, getChartConfig } from 'components/Dashboard/shared/utils';
import { TKpiData, TKpiLabelFormat } from '../types/kpi';

/**
 * Parse KPI data in relation to the widget configuration.
 * Handles both text-based and numeric KPIs with proper formatting and comparison logic.
 *
 * @param widget - The widget configuration
 * @param data - The raw data response from the API
 * @param showFraction - Whether to show fractional changes
 * @param isSuccess - Whether the data fetch was successful
 * @param isMobileOrTablet - Whether the device is mobile or tablet (affects number notation)
 * @returns Parsed and formatted KPI data
 */
export const parseKpiData = ({
  widget,
  data,
  showFraction,
  isSuccess,
  isMobileOrTablet,
}: {
  widget: TWidget;
  data: any;
  showFraction: boolean;
  isSuccess: boolean;
  isMobileOrTablet: boolean;
}): TKpiData => {
  const initKpi: TKpiData = {
    currentValue: null,
    difference: null,
    displayText: '',
    hasNullResults: false,
    hasResults: false,
    isComplete: false,
    isLabelKpi: false,
    isPriorValueNull: false,
    isTrendingUp: false,
    percentageChange: '',
    priorValue: 0,
    sentiment: 'standard' as const,
    subtext: '',
    tooltipText: '',
    value: null,
  };

  if (!isSuccess) {
    return initKpi;
  }

  const locale = i18next.language;
  const module: TModule = widget?.module;

  // Get the display configuration. Keys correspond with the results.
  // Sentiment informs how to interpret the trend and display the arrow icon ('standard' || 'opposite').
  const { config: kpiDisplayOptionConfig } = getChartConfig(widget);
  const {
    current: currentKey,
    prior: priorKey,
    sentiment = 'standard' as const, // 'standard' | 'opposite'
  } = kpiDisplayOptionConfig || {};

  // Validate required keys are present.
  if (!currentKey) {
    return initKpi;
  }

  // Dataset results--for KPIs it will always be the first item.
  const results: Record<string, any> = data?.results?.[0];
  const hasResults: boolean = data?.total > 0;

  // Check if the KPI is a simple string that will display text instead of a numerical value.
  // Only currentValue will exist in the data response to be displayed.
  const labelAndTypeConfig: Record<string, { label: string; type: string }> =
    module?.data;
  const isTextKpi: boolean =
    !priorKey || labelAndTypeConfig?.[currentKey]?.type === 'string';

  // If it's not a text KPI and priorKey is missing, we cannot parse the KPI.
  if (!isTextKpi && !priorKey) {
    return initKpi;
  }

  // Casting priorKey to string for results access -- covered in check above.
  const priorKeyString = priorKey as string;

  // Check if both current and prior keys have null results.
  const hasNullResults = isTextKpi
    ? results?.[currentKey] === null
    : results?.[currentKey] === null && results?.[priorKeyString] === null;

  // Check if there is a subtext to display.
  const subtextKey =
    !!results && !hasNullResults && !!Object.keys(results).length
      ? Object.keys(results).find((key) => key.includes('total_active_'))
      : null;
  const subtext = subtextKey
    ? `of ${results[subtextKey]} total ${subtextKey.split('_')[2]}`
    : '';

  // If there are null values, don't display the KPI.
  if (hasNullResults) {
    return {
      ...initKpi,
      hasNullResults,
      hasResults,
      isComplete: true,
    };
  }

  const currentValue: number | string = results?.[currentKey];

  // Handle text-based KPIs (simple string display).
  if (isTextKpi) {
    const maxStringLength = 15;
    const currentValueString: string = currentValue?.toString() || '';
    const normalizedValue =
      currentValueString === '(direct)' ? 'Direct' : currentValueString;
    const displayText =
      normalizedValue.length > maxStringLength
        ? `${normalizedValue.substring(0, maxStringLength)}...`
        : normalizedValue;

    return {
      ...initKpi,
      displayText,
      hasNullResults,
      hasResults,
      isComplete: true,
      isLabelKpi: true,
      sentiment: sentiment as 'standard' | 'opposite',
      subtext,
      value: normalizedValue,
    };
  }

  // Handle numeric KPIs.
  const priorValue: number | string = results?.[priorKeyString];
  const currentValueNumber = Number(currentValue);
  const priorValueNumber = Number(priorValue);

  // Get the current and prior key configurations, to format the value properly (currency, percent, etc.).
  const currentKeyFormatConfig: TKpiLabelFormat = module?.data?.[currentKey];
  const priorKeyFormatConfig: TKpiLabelFormat = module?.data?.[priorKeyString];

  // Format the current value (for KPIs, always base off of currentKey/value), which is displayed in the lower left corner.
  const defaultPrecision = getDefaultPrecision(currentValueNumber);

  const formattedCurrentValue: string = formatChartValue({
    value: currentValueNumber,
    format: currentKeyFormatConfig?.format,
    precision: currentKeyFormatConfig?.precision ?? defaultPrecision,
    notation: isMobileOrTablet ? 'compact' : 'standard',
    locale,
  });

  // Format the prior value (for KPIs, always base off of priorKey/value), which is displayed in the tooltip.
  const formattedPriorValue: string = formatChartValue({
    value: priorValueNumber,
    format: priorKeyFormatConfig?.format,
    precision: priorKeyFormatConfig?.precision ?? defaultPrecision,
  });

  const formattedCurrentTooltipValue: string = formatChartValue({
    value: currentValueNumber,
    format: currentKeyFormatConfig?.format,
    precision: currentKeyFormatConfig?.precision ?? defaultPrecision,
    notation: 'standard',
    locale,
  });

  const formattedPriorTooltipValue: string = formatChartValue({
    value: priorValueNumber,
    format: currentKeyFormatConfig?.format,
    precision: currentKeyFormatConfig?.precision ?? defaultPrecision,
    notation: 'standard',
    locale,
  });

  // Check trending status, this will determine the arrow icon in correlation with "sentiment".
  const isTrendingUp: boolean = currentValueNumber - priorValueNumber > 0;

  // Get the percentage change.
  const percentageChange = getKpiPercentageChange(
    currentValueNumber,
    priorValueNumber,
    showFraction,
  );

  return {
    currentValue: currentValueNumber,
    difference: currentValueNumber - priorValueNumber,
    displayText: formattedCurrentValue,
    hasNullResults,
    hasResults,
    isComplete: true,
    isLabelKpi: false,
    isPriorValueNull: priorValue === null,
    isTrendingUp,
    percentageChange,
    priorValue: formattedPriorValue,
    sentiment: sentiment as 'standard' | 'opposite',
    subtext,
    tooltipText: `Currently: ${formattedCurrentTooltipValue} | Previously: ${
      priorValue === null ? '?' : formattedPriorTooltipValue
    }`,
    value: formattedCurrentValue,
  };
};
